import classNames from "classnames"
import PropTypes from "prop-types"
import React from "react"

import ToggleGroup from "src/components/ToggleGroup"

import { useQueryParamState } from "src/hooks/use_query_param_state"

const ALIGN_CLASSES = {
  left: "text-left",
  right: "text-right",
}

const pixels = (size) => (size?.endsWith("px") ? parseFloat(size) : 0)

// Like DataTableV2, the table scrolls once its columns stop fitting; the point
// comes from the column widths rather than a fixed floor.
const tableMinWidth = ([railColumn, ...otherColumns]) =>
  pixels(railColumn.width) +
  pixels(railColumn.minWidth) +
  otherColumns.reduce((total, column) => total + pixels(column.width), 0)

const railStyle = (index, count) => ({
  top: index === 0 ? "50%" : "0px",
  bottom: index === count - 1 ? "50%" : "0px",
})

const Marker = ({ icon }) =>
  icon ? (
    <span
      data-testid="timeline-marker"
      data-marker="icon"
      className="absolute left-1/2 top-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700"
    >
      <i className={icon} aria-hidden="true" />
    </span>
  ) : (
    <span
      data-testid="timeline-marker"
      data-marker="dot"
      className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-400"
    />
  )

Marker.propTypes = {
  icon: PropTypes.string,
}

const TimelineTable = ({ label, columns, items, emptyMessage }) => {
  const [railColumn, ...otherColumns] = columns

  return (
    <div
      className="relative overflow-x-auto rounded border bg-white"
      data-design-system="Timeline"
    >
      <table
        aria-label={label}
        className="w-full table-fixed"
        style={{ minWidth: `${tableMinWidth(columns)}px` }}
      >
        <thead>
          <tr className="h-10 border-b bg-gray-200">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={classNames(
                  "overflow-hidden whitespace-nowrap px-4 font-semibold",
                  ALIGN_CLASSES[column.align || "left"]
                )}
                style={{ width: column.width }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-6 text-center text-gray-600"
              >
                {emptyMessage}
              </td>
            </tr>
          )}
          {items.map((item, index) => (
            <tr key={item.id}>
              <td className="overflow-hidden px-4" style={{ height: "1px" }}>
                <div className="flex h-full min-h-[48px] items-stretch">
                  <div className="relative w-8 shrink-0">
                    {items.length > 1 && (
                      <span
                        data-testid="timeline-rail"
                        className="absolute left-1/2 w-px -translate-x-1/2 bg-gray-300"
                        style={railStyle(index, items.length)}
                      />
                    )}
                    <Marker icon={item.icon} />
                  </div>
                  <div className="min-w-0 flex-1 self-center py-3 pl-3">
                    {railColumn.render(item)}
                  </div>
                </div>
              </td>
              {otherColumns.map((column) => (
                <td
                  key={column.key}
                  className={classNames(
                    "overflow-hidden px-4 py-3",
                    ALIGN_CLASSES[column.align || "left"]
                  )}
                >
                  {column.render(item)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const columnsShape = PropTypes.arrayOf(
  PropTypes.shape({
    key: PropTypes.string.isRequired,
    header: PropTypes.node.isRequired,
    render: PropTypes.func.isRequired,
    align: PropTypes.oneOf(Object.keys(ALIGN_CLASSES)),
    width: PropTypes.string,
    minWidth: PropTypes.string,
  })
)

const itemsShape = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    icon: PropTypes.string,
  })
)

TimelineTable.propTypes = {
  label: PropTypes.string.isRequired,
  columns: columnsShape.isRequired,
  items: itemsShape.isRequired,
  emptyMessage: PropTypes.string.isRequired,
}

const filtersShape = PropTypes.arrayOf(
  PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    matches: PropTypes.func,
  })
)

// The default filter stays out of the URL, so a plain link opens the default.
const FilteredTimeline = ({
  heading,
  filters,
  defaultFilter = filters[0].value,
  filterParam,
  onFilterChange,
  items,
  ...tableProps
}) => {
  const [param, setParam] = useQueryParamState(filterParam, {
    defaultValue: "",
  })
  const selected =
    filters.find((filter) => filter.value === param) ||
    filters.find((filter) => filter.value === defaultFilter)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        {heading}
        <div className="ml-auto flex items-center gap-2 text-sm text-gray-700">
          <span aria-hidden="true">Show:</span>
          <ToggleGroup
            variant="pills"
            ariaLabel="Show"
            items={filters.map(({ value, label }) => ({ value, label }))}
            value={selected.value}
            onChange={(value) => {
              setParam(value === defaultFilter ? "" : value)
              onFilterChange?.(value)
            }}
          />
        </div>
      </div>
      <TimelineTable
        {...tableProps}
        items={selected.matches ? items.filter(selected.matches) : items}
      />
    </div>
  )
}

FilteredTimeline.propTypes = {
  heading: PropTypes.node,
  filters: filtersShape.isRequired,
  defaultFilter: PropTypes.string,
  filterParam: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func,
  items: itemsShape.isRequired,
}

const Timeline = ({
  heading,
  filters,
  defaultFilter,
  filterParam = "show",
  onFilterChange,
  emptyMessage = "Nothing to show",
  ...tableProps
}) =>
  filters ? (
    <FilteredTimeline
      {...tableProps}
      heading={heading}
      filters={filters}
      defaultFilter={defaultFilter}
      filterParam={filterParam}
      onFilterChange={onFilterChange}
      emptyMessage={emptyMessage}
    />
  ) : heading ? (
    <div className="flex flex-col gap-3">
      {heading}
      <TimelineTable {...tableProps} emptyMessage={emptyMessage} />
    </div>
  ) : (
    <TimelineTable {...tableProps} emptyMessage={emptyMessage} />
  )

Timeline.propTypes = {
  label: PropTypes.string.isRequired,
  columns: columnsShape.isRequired,
  items: itemsShape.isRequired,
  heading: PropTypes.node,
  filters: filtersShape,
  defaultFilter: PropTypes.string,
  filterParam: PropTypes.string,
  onFilterChange: PropTypes.func,
  emptyMessage: PropTypes.string,
}

export default Timeline
